import React, { memo, useCallback, useMemo, useRef } from "react";

import { List } from "react-window";

import type { JSX } from "react";
import type { GroupBase, MenuListProps } from "react-select";
import type { ListImperativeAPI, RowComponentProps } from "react-window";

const OPTION_HEIGHT = 35;

interface MenuListRowProps {
  children: React.ReactNode[];
}

function MenuListRowComponent({
  index,
  style,
  children,
}: RowComponentProps<MenuListRowProps>): JSX.Element {
  return <div style={style}>{children[index]}</div>;
}

MenuListRowComponent.displayName = "MenuListRow";

const MenuListRow = memo(MenuListRowComponent) as typeof MenuListRowComponent;

function VirtualizedSelectMenuList<
  Option,
  IsMulti extends boolean = boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  children,
  innerProps,
  innerRef,
  maxHeight,
}: MenuListProps<Option, IsMulti, Group>): JSX.Element | null {
  const listInstanceRef = useRef<ListImperativeAPI | null>(null);

  const options = useMemo(() => React.Children.toArray(children), [children]);
  const height = Math.min(maxHeight, options.length * OPTION_HEIGHT);

  const setInnerRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (typeof innerRef === "function") {
        innerRef(node);
        return;
      }

      if (innerRef) {
        // eslint-disable-next-line no-param-reassign
        (innerRef as React.MutableRefObject<HTMLDivElement | null>).current =
          node;
      }
    },
    [innerRef],
  );

  const setListRef = useCallback(
    (instance: ListImperativeAPI | null) => {
      listInstanceRef.current = instance;
      setInnerRef(instance?.element ?? null);
    },
    [setInnerRef],
  );

  if (!options.length) {
    return null;
  }

  return (
    <List<MenuListRowProps>
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...innerProps}
      listRef={setListRef}
      rowComponent={MenuListRow}
      rowCount={options.length}
      rowHeight={OPTION_HEIGHT}
      rowProps={{ children: options }}
      style={{ height }}
    />
  );
}

export default VirtualizedSelectMenuList;
